import { useEffect, useState, useCallback } from "react";
import { Container, Form, Badge, Card, Row, Col, Button, Accordion } from "react-bootstrap";
import { getMatchList, getIntervalloRefresh } from "../api/api.js";

export default function ListaMatchPerArea() {
  const [UPDATE_INTERVAL, setUpdateInterval] = useState(10);
  const [countdown, setCountdown] = useState(10);
  const [aree, setAree] = useState([]);
  const [selectedArea, setSelectedArea] = useState(() => localStorage.getItem("selectedArea") || "");
  const [filter, setFilter] = useState("");
  const [language, setLanguage] = useState("it");

  const translations = {
    it: {
      updating: "Aggiornamento automatico in",
      selectArea: "Seleziona Area",
      searchPlaceholder: "Cerca match...",
      current: "Corrente",
      winner: "Vincitore",
      noMatches: "Nessun abbinamento disponibile.",
      selectToView: "Seleziona un'area per vedere i match.",
      match: "Match",
      tie: "Pareggio",
      finished: "Match conclusi",
    },
    en: {
      updating: "Auto refresh in",
      selectArea: "Select Area",
      searchPlaceholder: "Search match...",
      current: "Current",
      winner: "Winner",
      noMatches: "No matches available.",
      selectToView: "Select an area to view matches.",
      match: "Match",
      tie: "Tie",
      finished: "Finished matches",
    },
  };

  const t = (key) => translations[language][key];

  const fetchInterval = useCallback(async () => {
    try {
      const res = await getIntervalloRefresh();
      if (res.ok && res.data?.value) {
        setUpdateInterval(res.data.value);
        setCountdown(res.data.value);
      }
    } catch (err) {
      console.error("Errore caricamento intervallo:", err);
    }
  }, []);

  const fetchAree = useCallback(async () => {
    try {
      const res = await getMatchList();
      if (res.ok) {
        setAree(res.data);

        if (!selectedArea && res.data.length > 0) {
          const first = res.data[0].id.toString();
          setSelectedArea(first);
          localStorage.setItem("selectedArea", first);
        }
      }
      setCountdown(UPDATE_INTERVAL);
    } catch (err) {
      console.error("Errore caricamento aree:", err);
    }
  }, [selectedArea, UPDATE_INTERVAL]);

  useEffect(() => {
    fetchAree();
    fetchInterval();
  }, [fetchAree, fetchInterval]);

  useEffect(() => {
    if (!UPDATE_INTERVAL) return;
    setCountdown(UPDATE_INTERVAL);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchAree();
          return UPDATE_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [UPDATE_INTERVAL, fetchAree]);

  const handleSelectArea = (value) => {
    setSelectedArea(value);
    localStorage.setItem("selectedArea", value);
  };

  const areaCorrente = aree.find((a) => a.id.toString() === selectedArea);
  const normalize = (str) => str.replace(/\s+/g, "").toLowerCase();

  const renderCard = (ab) => {
    const isCorrente = areaCorrente.match_attuale === ab.match_numero;
    const haVincitore = ab.vincitore && ab.vincitore !== "";
    const isRedWinner = haVincitore && normalize(ab.vincitore) === normalize(ab.palestra_r + ab.atleta_r);
    const isBlueWinner = haVincitore && normalize(ab.vincitore) === normalize(ab.palestra_b + ab.atleta_b);
    const isTie = haVincitore && normalize(ab.vincitore) === normalize("pareggio");

    return (
      <Card
        className={`shadow-sm ${isCorrente ? "border border-warning border-3" : ""}`}
        style={{ backgroundColor: isCorrente ? "#fff3b0" : "#fff" }}
      >
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold mb-0">
              {t("match")} {ab.match_numero}{" "}
              {isCorrente && <Badge bg="warning" text="dark">{t("current")}</Badge>}
            </h5>
          </div>

          {ab.dettaglio && (
            <div className="mb-2 fw-semibold text-muted" style={{ fontSize: "0.85rem" }}>
              {ab.dettaglio}
            </div>
          )}

          <Row>
            <Col xs={6} className="text-center">
              <div className="p-2 rounded" style={{ backgroundColor: "#f8d7da" }}>
                {isTie && <Badge bg="warning" text="light" className="mb-1">{t("tie")}</Badge>}
                {isRedWinner && <Badge bg="success" text="light" className="mb-1">{t("winner")}</Badge>}
                <span className="fw-bold d-block">{ab.palestra_r}</span>
                <small className="text-muted">{ab.atleta_r}</small>
              </div>
            </Col>

            <Col xs={6} className="text-center">
              <div className="p-2 rounded" style={{ backgroundColor: "#cfe2ff" }}>
                {isTie && <Badge bg="warning" text="light" className="mb-1">{t("tie")}</Badge>}
                {isBlueWinner && <Badge bg="success" text="light" className="mb-1">{t("winner")}</Badge>}
                <span className="fw-bold d-block">{ab.palestra_b}</span>
                <small className="text-muted">{ab.atleta_b}</small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Container className="my-4">
      <div className="text-end mb-3">
        <Button variant="light" onClick={() => setLanguage(language === "it" ? "en" : "it")}>
          {language === "it" ? "🇬🇧 English" : "🇮🇹 Italiano"}
        </Button>
      </div>

      <p className="text-center text-danger fw-bold small mb-3">
        {t("updating")} {countdown}s
      </p>

      <h1 className="h4 mb-3 text-center text-md-start">{t("selectArea")}</h1>

      <Form.Select
        value={selectedArea}
        onChange={(e) => handleSelectArea(e.target.value)}
        className="mb-3 shadow-sm rounded"
      >
        {aree.map((area) => (
          <option key={area.id} value={area.id}>
            {area.nome}
          </option>
        ))}
      </Form.Select>

      <Form.Control
        type="text"
        placeholder={t("searchPlaceholder")}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="mb-4 shadow-sm rounded"
      />

      {areaCorrente ? (
        <>
          <Card className="mb-4 shadow-sm border border-primary border-2">
            <Card.Body className="text-center py-3">
              <h2 className="fw-bold mb-2" style={{ fontSize: "1.8rem" }}>
                {areaCorrente.nome}
              </h2>

              {areaCorrente.categoria && (
                <Badge
                  bg="primary"
                  className="px-4 py-2"
                  style={{ fontSize: "1rem", borderRadius: "0.5rem" }}
                >
                  {areaCorrente.categoria}
                </Badge>
              )}
            </Card.Body>
          </Card>

          {/* ABBINAMENTI ATTIVI */}
          <Row className="g-3 mb-4">
            {areaCorrente.abbinamenti
              .filter(ab => !ab.vincitore || ab.vincitore === "")
              .filter((ab) =>
                `${ab.match_numero} ${ab.palestra_r} ${ab.atleta_r} ${ab.palestra_b} ${ab.atleta_b}`
                  .toLowerCase()
                  .includes(filter.toLowerCase())
              )
              .map((ab) => (
                <Col xs={12} md={6} key={ab.id}>
                  {renderCard(ab)}
                </Col>
              ))}
          </Row>

          {/* MATCH CONCLUSI (Collapse individuale) */}
          <h5 className="mb-3">{t("finished")}</h5>

          <Accordion alwaysOpen>
            {areaCorrente.abbinamenti
              .filter(ab => ab.vincitore && ab.vincitore !== "")
              .filter((ab) =>
                `${ab.match_numero} ${ab.palestra_r} ${ab.atleta_r} ${ab.palestra_b} ${ab.atleta_b}`
                  .toLowerCase()
                  .includes(filter.toLowerCase())
              )
              .map((ab) => (
                <Accordion.Item eventKey={ab.id.toString()} key={ab.id} className="mb-2">
                  <Accordion.Header>
                    {t("match")} {ab.match_numero} – {t("winner")}: {ab.vincitore}
                  </Accordion.Header>
                  <Accordion.Body>
                    {renderCard(ab)}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
          </Accordion>
        </>
      ) : (
        <p className="text-center text-muted">{t("selectToView")}</p>
      )}
    </Container>
  );
}
